import React, { useMemo, useState } from "react";
import {
    Dropdown,
    DropdownToggle,
    DropdownMenu,
    DropdownItem
} from "reactstrap";

export interface SegmentedOption<T extends string = string> {
    key: T;
    label: string;
}

interface SegmentedSwitcherProps<T extends string = string> {
    options: SegmentedOption<T>[];
    value: T;
    onChange: (value: T) => void;
    visibleCount?: number; // how many pills to show before "..."
    className?: string;
}

function SegmentedSwitcher<T extends string = string>({
    options,
    value,
    onChange,
    visibleCount = 5,
    className = ""
}: SegmentedSwitcherProps<T>) {
    const [dropdownOpen, setDropdownOpen] = useState(false);

    const toggle = () => setDropdownOpen(v => !v);

    // Ensure active option is always visible
    const orderedOptions = useMemo(() => {
        if(options.length <= visibleCount) return options;
        const active = options.find(o => o.key === value);
        const rest = options.filter(o => o.key !== value);

        if (!active) return options;

        return [active, ...rest];
    }, [options, value]);

    const visibleOptions = orderedOptions.slice(0, visibleCount);
    const hiddenOptions = orderedOptions.slice(visibleCount);

    return (
        <div
            className={`d-inline-flex align-items-center rounded-pill bg-white p-1 ${className}`}
        >
            {/* Visible pills */}
            {visibleOptions.map(opt => {
                const isActive = opt.key === value;

                return (
                    <button
                        key={opt.key}
                        className={`btn btn-sm rounded-pill px-3 me-1 ${isActive
                                ? "btn-primary theme-btn fw-semibold"
                                : "btn-light text-muted"
                            }`}
                        style={{
                            border: "none"
                        }}
                        onClick={() => onChange(opt.key)}
                    >
                        {opt.label}
                    </button>
                );
            })}

            {/* More dropdown */}
            {hiddenOptions.length > 0 && (
                <Dropdown isOpen={dropdownOpen} toggle={toggle}>
                    <DropdownToggle
                        tag="button"
                        className="btn btn-sm btn-light rounded-pill px-3 py-0"
                        style={{ border: "none" }}
                    >
                        <i className="bx bx-dots-horizontal-rounded fs-4 text-muted fw-semibold" style={{marginTop: '4px'}}></i>
                    </DropdownToggle>

                    <DropdownMenu end>
                        {hiddenOptions.map(opt => (
                            <DropdownItem
                                key={opt.key}
                                active={opt.key === value}
                                onClick={() => {
                                    onChange(opt.key);
                                    setDropdownOpen(false);
                                }}
                            >
                                {opt.label}
                            </DropdownItem>
                        ))}
                    </DropdownMenu>
                </Dropdown>
            )}
        </div>
    );
}

export default SegmentedSwitcher;
